import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { applyOpenTalentJob } from "../../../../redux/features/find-a-job/actions/authAction";
import PdfOrImageUploader from "../../../pdfImage uploader";
import ErrorMessage from "../../../form/ErrorMessage";
import TagInput from "../../../form/TagInput";
import { getProfile } from "../../../../utils/pip";
import "./ApplyNowSignupModal.css";

const ApplyNowPrefilledModal = ({
  isOpen = true,
  onClose,
  onSubmit,
  onOpenSignIn,
  formData = {},
}) => {
  const dispatch = useDispatch();
  const { isLoading, freelancerProfileData } = useSelector(
    (state) => state?.freelancer?.auth || {}
  );
  const profileData =
    freelancerProfileData && Object.keys(freelancerProfileData).length
      ? freelancerProfileData
      : getProfile("freelancer") || {};
  const splitList = (value) =>
    String(value || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  const cvValue = profileData?.cv || profileData?.open_talent?.cv;
  const existingCvList = Array.isArray(cvValue) ? cvValue : cvValue ? [cvValue] : [];
  const workOptions = [
    { label: "Remote", value: "Remote Freelance" },
    { label: "Full-time", value: "Full-time Freelance" },
    { label: "Part-time", value: "Part-time Freelance" },
    { label: "Hybrid", value: "Hybrid Freelance" },
    { label: "On-Site", value: "On-Site Freelance" },
    { label: "Project-Based", value: "Project Based" },
  ];
  const [formValues, setFormValues] = React.useState({
    full_name: formData?.full_name || "",
    email: formData?.email || "",
    phone_number: "+91",
    linkedin_url: "",
    portfolio_url: "",
    cover_letter: "",
    // skills: splitList(profileData?.open_talent?.skills || profileData?.open_talent?.key_skills),
    expected_salary: "",
    work_type: "Monthly",
    current_position: "",
    resume_files: existingCvList,
  });
  const [formErrors, setFormErrors] = React.useState({});
  const [apiError, setApiError] = React.useState("");

  const handleOverlayClick = (event) => {
    event.stopPropagation();
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  useEffect(() => {
    setFormValues((prev) => {
      if (prev?.resume_files?.length) return prev;
      return { ...prev, resume_files: existingCvList };
    });
  }, [existingCvList]);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const resolveResumeValue = (resumeFiles = []) => {
    const firstItem = resumeFiles?.[0];
    if (!firstItem) return null;
    if (firstItem instanceof File) return firstItem;
    if (typeof firstItem === "string") return firstItem;
    if (firstItem?.file instanceof File) return firstItem.file;
    if (typeof firstItem?.file === "string") return firstItem.file;
    if (typeof firstItem?.url === "string") return firstItem.url;
    if (typeof firstItem?.preview === "string") return firstItem.preview;
    return null;
  };

  const validateForm = () => {
    const errors = {};
    const checkUrl = (value) => {
      try {
        // eslint-disable-next-line no-new
        new URL(value);
        return true;
      } catch (e) {
        return false;
      }
    };

    if (!String(formValues.full_name || "").trim()) {
      errors.full_name = "Please enter full name";
    }

    if (!String(formValues.email || "").trim()) {
      errors.email = "Please enter email";
    }

    if (!formValues.phone_number) {
      errors.phone_number = "Please enter phone number";
    } else if (!isValidPhoneNumber(formValues.phone_number)) {
      errors.phone_number = "Please enter valid phone number";
    }
    if (!String(formValues.expected_salary || "").trim()) errors.expected_salary = "Please enter expected salary";
    if (!String(formValues.work_type || "").trim()) errors.work_type = "Please select work";
    if (!String(formValues.current_position || "").trim()) errors.current_position = "Please enter current position";
    // if (!Array.isArray(formValues.skills) || formValues.skills.length === 0) {
    //   errors.skills = "Please add at least one skill";
    // }

    if (!String(formValues.linkedin_url || "").trim()) {
      errors.linkedin_url = "Please enter Linkedin profile link";
    } else if (!checkUrl(String(formValues.linkedin_url || "").trim())) {
      errors.linkedin_url = "Please enter valid Linkedin profile link";
    }

    if (!String(formValues.portfolio_url || "").trim()) {
      errors.portfolio_url = "Please enter portfolio link";
    } else if (!checkUrl(String(formValues.portfolio_url || "").trim())) {
      errors.portfolio_url = "Please enter valid portfolio link";
    }

    if (!String(formValues.cover_letter || "").trim()) errors.cover_letter = "Please enter cover letter";
    if (!Array.isArray(formValues.resume_files) || formValues.resume_files.length === 0) {
      errors.resume_files = "Please upload resume";
    }

    return errors;
  };

  const updateField = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: "" }));
    setApiError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    setApiError("");
    if (Object.keys(errors).length) return;

    const payload = new FormData();
    payload.append("job_id", String(formData?.job_id || ""));
    payload.append("full_name", String(formValues.full_name || ""));
    payload.append("email", String(formValues.email || ""));
    payload.append("phone_number", String(formValues.phone_number || ""));
    payload.append("expected_salary", String(formValues.expected_salary || ""));
    payload.append("work_type", String(formValues.work_type || "Monthly"));
    payload.append("current_position", String(formValues.current_position || ""));
    payload.append("linkedin_url", String(formValues.linkedin_url || ""));
    payload.append("portfolio_url", String(formValues.portfolio_url || ""));
    payload.append("cover_letter", String(formValues.cover_letter || ""));
    // payload.append("skills", (formValues.skills || []).join(", "));
    const resumeValue = resolveResumeValue(formValues.resume_files);
    if (resumeValue) {
      payload.append("resume_url", resumeValue);
    }

    dispatch(
      applyOpenTalentJob({
        payload,
        token: formData?.auth_token || "",
        callback: (response) => {
          const isSuccess = response?.success === true || response?.status === true;
          if (!isSuccess) {
            setApiError(response?.message || "Failed to submit application.");
            return;
          }
          if (isSuccess && onSubmit) {
            onSubmit(response);
          }
        },
      })
    );
  };

  return createPortal(
    <div className="job-modal-overlay" onClick={handleOverlayClick}>
      <div className="job-modal" role="dialog" aria-modal="true">
        <div className="job-modal-header">
          <h2 className="job-modal-title ct_fs_30">
            {formData?.job_title || "[Job Title]"}{" - "}
            {formData?.company_name || "[Company Name]"}
          </h2>

          <button
            type="button"
            className="job-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4.66699 4.66699L11.3337 11.3337M11.3337 4.66699L4.66699 11.3337"
                stroke="#023047"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <form className="job-modal-body" onSubmit={handleSubmit}>
          <div className="job-grid">
            <div className="job-field job-field-full">
              <label className="job-label">Full Name</label>
              <input
                className="job-input"
                type="text"
                value={formValues.full_name}
                onChange={(event) => updateField("full_name", event.target.value)}
              />
              {formErrors.full_name ? (
                <p className="ct_modal_input_error">{formErrors.full_name}</p>
              ) : null}
            </div>

            <div className="job-field">
              <label className="job-label">Email Address</label>
              <input
                className="job-input"
                type="text"
                value={formValues.email}
                onChange={(event) => updateField("email", event.target.value)}
                readOnly
                disabled
              />
              {formErrors.email ? (
                <p className="ct_modal_input_error">{formErrors.email}</p>
              ) : null}
            </div>

            <div className="job-field">
              <label className="job-label">Phone Number</label>
              <div className="job-phone-row">
                <PhoneInput
                  id="phone_number"
                  className="form-control ct_input ct_phone_input_no_14"
                  defaultCountry="IN"
                  international
                  countryCallingCodeEditable
                  value={formValues.phone_number}
                  onChange={(value) => updateField("phone_number", value || "")}
                />
              </div>
              {formErrors.phone_number ? (
                <p className="ct_modal_input_error">{formErrors.phone_number}</p>
              ) : null}
            </div>
          </div>

          <div className="job-grid">
            <div className="job-field">
              <label className="job-label">Expected Salary</label>
              <input
                className="job-input"
                type="number"
                value={formValues.expected_salary}
                onChange={(event) => updateField("expected_salary", event.target.value)}
              />
              {formErrors.expected_salary ? (
                <p className="ct_modal_input_error">{formErrors.expected_salary}</p>
              ) : null}
            </div>
            <div className="job-field">
              <label className="job-label">Work</label>
              <div className="job-select-wrap">
                <select
                  value={formValues.work_type}
                  className="job-input job-select"
                  onChange={(event) => updateField("work_type", event.target.value)}
                >
                  <option value="">Select Work</option>
                  {workOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="job-chevron">
                  <svg width="14" height="9" viewBox="0 0 14 9" fill="none">
                    <path
                      d="M1.16699 1.25L7.00033 7.08333L12.8337 1.25"
                      stroke="#023047"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
              {formErrors.work_type ? (
                <p className="ct_modal_input_error">{formErrors.work_type}</p>
              ) : null}
            </div>
          </div>

          <div className="job-field job-field-full">
            <label className="job-label">Current Position (if unemployed, then “Unemployed”)</label>
            <input
              className="job-input"
              type="text"
              value={formValues.current_position}
              onChange={(event) => updateField("current_position", event.target.value)}
            />
            {formErrors.current_position ? (
              <p className="ct_modal_input_error">{formErrors.current_position}</p>
            ) : null}
          </div>

          {/* <TagInput
            label="Skills"
            placeholder="Type to add skills..."
            name="skills"
            value={formValues.skills}
            onChange={updateField}
            error={formErrors.skills}
            addOnBlur={true}
          /> */}

          <div className="job-field job-field-full">
            <label className="job-label">Linkedin Profile Link</label>
            <input
              className="job-input"
              type="url"
              value={formValues.linkedin_url}
              onChange={(event) => updateField("linkedin_url", event.target.value)}
            />
            {formErrors.linkedin_url ? (
              <p className="ct_modal_input_error">{formErrors.linkedin_url}</p>
            ) : null}
          </div>

          <div className="job-field job-field-full">
            <label className="job-label">Portfolio Link</label>
            <input
              className="job-input"
              type="url"
              value={formValues.portfolio_url}
              onChange={(event) => updateField("portfolio_url", event.target.value)}
            />
            {formErrors.portfolio_url ? (
              <p className="ct_modal_input_error">{formErrors.portfolio_url}</p>
            ) : null}
          </div>

          <div className="job-field job-field-full">
            <label className="job-label">Cover Letter</label>
            <textarea
              className="job-input job-textarea h-auto" rows={4}
              value={formValues.cover_letter}
              onChange={(event) => updateField("cover_letter", event.target.value)}
            />
            {formErrors.cover_letter ? (
              <p className="ct_modal_input_error">{formErrors.cover_letter}</p>
            ) : null}
          </div>

          <div className="job-field job-field-full">
            <label className="job-label">Upload Resume</label>
            <PdfOrImageUploader
              maxFiles={1}
              multiple={false}
              acceptType="application/pdf,.pdf"
              placeholder="Upload CV (PDF only)"
              replaceOnSelect={true}
              existingFiles={
                formValues?.resume_files && Array.isArray(formValues.resume_files)
                  ? formValues.resume_files.map((file, idx) => ({
                    id: file?.id || idx,
                    file: file,
                    name:
                      file?.name ||
                      (typeof file === "string"
                        ? file.split("/").pop()
                        : `cv-${idx + 1}.pdf`),
                    type: "pdf",
                    preview:
                      typeof file === "string"
                        ? file
                        : file?.preview || file?.url || "",
                    isExisting: !(file instanceof File),
                  }))
                  : []
              }
              onChange={(newFiles) => updateField("resume_files", newFiles)}
            />
            <ErrorMessage
              errors={formErrors}
              touched={{ resume_files: true }}
              fieldName="resume_files"
            />
          </div>

          {apiError ? <p className="ct_modal_input_error mt-1">{apiError}</p> : null}

          <div className="job-submit-wrap">
            <button className="job-submit-btn" type="submit" disabled={isLoading}>
              {isLoading ? "Please wait..." : "Submit"}
            </button>
          </div>

          <p className="job-footer-note">
            Increase your chances by <span className="job-orange">3x</span> with a{" "}
            <span
              className="job-yellow"
              style={{ cursor: "pointer" }}
              onClick={() => onOpenSignIn?.()}
            >
              NUVIO profile
            </span>
          </p>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ApplyNowPrefilledModal;
